import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  FileText,
  Info,
  UploadCloud,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  FORM_DATE_FIELD_CLASS,
  FORM_FIELD_CLASS,
  FORM_TEXTAREA_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/DatePicker'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import {
  isoDateToLocalDate,
  localDateToIsoDate,
  normalizeToIsoDate,
} from '@/lib/date'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import {
  MAX_REPORT_PHOTOS,
  REPORT_AFFECTED_AREAS,
  REPORT_INFESTATION,
  REPORT_METHODS,
  REPORT_RECOMMENDATIONS,
  REPORT_SIGNS,
  REPORT_TREATED_AREAS,
  REPORT_URGENCY,
} from '@/lib/reportOptions'
import {
  useJob,
  useJobReport,
  useReportCatalog,
  useSaveJobReport,
} from '@/hooks/useJobs'
import { getApiErrorMessage } from '@/services/api'
import { jobsApi } from '@/services/jobs'
import materialUsedIcon from '@/assets/equipment/other.svg'
import productUsedIcon from '@/assets/report/product-used.svg'
import type { AttachmentRef } from '@/components/shared/types'
import { JOB_STATUS } from '@/types'

interface Product {
  name: string
  concentration: string
  quantity: string
}

interface Material {
  name: string
  quantity: string
}

interface ReportPhoto {
  fileId: string
  documentId?: string
  name: string
  previewUrl?: string
  mime_type?: string | null
  size?: number | null
  created_at?: string | null
}

type FollowUpSlot = 'morning' | 'afternoon' | 'indifferent'

const REPORT_PHOTO_ACCEPT = 'image/png,image/jpeg,image/jpg,application/pdf'

function isReportPhotoFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true
  if (file.type === 'application/pdf') return true
  return /\.(png|jpe?g|pdf)$/i.test(file.name)
}

const RADIO_TONE: Record<string, { dot: string; active: string }> = {
  Low: {
    dot: 'bg-[#10b981]',
    active: 'border-[#10b981] bg-[#ecfdf5] text-[#065f46]',
  },
  Moderate: {
    dot: 'bg-[#2e5ce6]',
    active: 'border-[#2e5ce6] bg-[#ebf1fe] text-[#2e5ce6]',
  },
  Medium: {
    dot: 'bg-[#2e5ce6]',
    active: 'border-[#2e5ce6] bg-[#ebf1fe] text-[#2e5ce6]',
  },
  High: {
    dot: 'bg-[#efac00]',
    active: 'border-[#efac00] bg-[#fef3c7] text-[#8a5405]',
  },
  Critical: {
    dot: 'bg-[#d63b33]',
    active: 'border-[#d63b33] bg-[#fee9e7] text-[#991b1b]',
  },
  Emergency: {
    dot: 'bg-[#d63b33]',
    active: 'border-[#d63b33] bg-[#fee9e7] text-[#991b1b]',
  },
}

function RadioDot({ option }: { option: string }) {
  return (
    <span
      className={cn(
        'h-2 w-2 shrink-0 rounded-full',
        RADIO_TONE[option]?.dot ?? 'bg-[#ff5a1f]'
      )}
    />
  )
}

function formatReportClockTime(
  value: string | null | undefined
): string | null {
  if (!value) return null
  const [hoursRaw, minutesRaw] = value.split(':')
  const hours = Number(hoursRaw)
  const minutes = Number(minutesRaw ?? '0')
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

function formatClientType(value: string | null | undefined): string | null {
  if (!value) return null

  return value
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toLocaleUpperCase() + word.slice(1))
    .join(' ')
}

function formatTrackingDate(
  isoDate: string,
  slotLabel: string,
  datePlaceholder: string,
  language: string | undefined
): string {
  const date = isoDateToLocalDate(isoDate)
  const locale = language?.startsWith('fr') ? 'fr-FR' : language || 'fr-FR'
  const dateLabel = date
    ? new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date)
    : datePlaceholder
  return slotLabel ? `${dateLabel}, ${slotLabel}` : dateLabel
}

// Bordered field group: orange left accent bar + title (+ optional action).
function SectionCard({
  title,
  required,
  count,
  action,
  bodyClassName,
  children,
}: {
  title: string
  required?: boolean
  count?: number
  action?: React.ReactNode
  bodyClassName?: string
  children: React.ReactNode
}) {
  return (
    <section className='space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <h3 className='flex items-center gap-2 font-sans text-sm font-semibold uppercase leading-[1.2] text-[#3d434e]'>
          <span className='h-5 w-[3px] shrink-0 rounded-full bg-[#ff5a1f]' />
          <span>{title}</span>
          {required && (
            <span className='font-sans text-sm font-semibold text-[#ff5a1f]'>
              *
            </span>
          )}
          {typeof count === 'number' && count > 0 && (
            <span className='inline-flex items-center gap-1 font-sans text-xs font-medium leading-4 text-[#ff5a1f]'>
              <span className='size-1.5 rounded-full bg-[#ff5a1f]' />
              {count}
            </span>
          )}
        </h3>
        {action}
      </div>
      <div className={cn('rounded-2xl bg-[#f5f6fa] p-4', bodyClassName)}>
        {children}
      </div>
    </section>
  )
}

// Single-select card row: leading icon + label + radio dot.
function RadioCardSelect({
  options,
  value,
  onChange,
  renderIcon,
  label,
  cols,
  activeClassName,
  labelClassName,
}: {
  options: readonly string[]
  value: string
  onChange: (next: string) => void
  renderIcon: (option: string, active: boolean) => React.ReactNode
  label: (option: string) => string
  cols: string
  activeClassName?: string
  labelClassName?: string
}) {
  void cols
  return (
    <div className='flex flex-wrap gap-2'>
      {options.map(option => {
        const active = value === option
        return (
          <button
            key={option}
            type='button'
            onClick={() => onChange(option)}
            className={cn(
              'flex h-9 min-w-0 items-center justify-center gap-2 rounded-xl border bg-white px-3 text-xs font-normal leading-4 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_1px_6px_rgba(38,45,66,0.08)] transition-colors',
              active
                ? (activeClassName ??
                    RADIO_TONE[option]?.active ??
                    'border-[#3271d7] bg-[#e6f0ff] text-[#194eb7]')
                : 'border-[#e7e9ef] text-foreground hover:bg-white'
            )}
          >
            <span className='flex min-w-0 items-center gap-2 text-left'>
              {renderIcon(option, active)}
              <span className={cn('truncate', labelClassName)}>
                {label(option)}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

// Toggleable checkbox pill for multi-select groups.
function CheckPill({
  label,
  active,
  onClick,
  className,
  icon,
  showCheck = true,
}: {
  label: string
  active: boolean
  onClick: () => void
  className?: string
  icon?: React.ReactNode
  showCheck?: boolean
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex h-9 min-w-0 items-center gap-2 rounded-xl border bg-white px-3 text-left text-xs font-normal leading-4 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_1px_6px_rgba(38,45,66,0.08)] transition-colors',
        active
          ? 'border-[#10b981] bg-[#ecfdf5] text-[#171a20]'
          : 'border-[#e7e9ef] text-foreground hover:bg-white',
        className
      )}
    >
      {showCheck && active && (
        <Check className='h-4 w-4 shrink-0 text-[#10b981]' />
      )}
      {icon}
      <span className='truncate'>{label}</span>
    </button>
  )
}

function OptionAssetIcon({ src }: { src: string }) {
  return (
    <span className='flex size-5 shrink-0 items-center justify-center overflow-hidden'>
      <img src={src} alt='' className='size-4 object-contain' />
    </span>
  )
}

function SelectableCard({
  label,
  active,
  onClick,
  description,
  className,
  compact = false,
  variant = 'checkbox',
}: {
  label: string
  active: boolean
  onClick: () => void
  description?: string
  className?: string
  compact?: boolean
  variant?: 'checkbox' | 'notification'
}) {
  const isNotification = variant === 'notification'

  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'rounded-xl border text-left shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors',
        isNotification
          ? 'flex min-h-[98px] items-start p-4'
          : compact
            ? 'min-h-[46px] px-4 py-3'
            : 'min-h-[68px] px-4 py-3',
        active
          ? 'border-[#10b981] bg-[#ecfdf5]'
          : 'border-[#e7e9ef] bg-white hover:bg-white',
        className
      )}
    >
      <span
        className={cn(
          'flex gap-3',
          isNotification ? 'items-start' : 'items-center'
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            isNotification ? 'size-[30px] rounded-lg' : 'size-4 rounded',
            active
              ? 'bg-[#10b981] text-white'
              : 'border border-[#b0b5c3] bg-white'
          )}
        >
          {active ? (
            <Check className={cn(isNotification ? 'size-4' : 'size-3.5')} />
          ) : (
            isNotification && <FileText className='size-4 text-[#1a1b1e]' />
          )}
        </span>
        <span className='min-w-0'>
          <span
            className={cn(
              'block text-sm font-medium leading-[1.4]',
              isNotification && active ? 'text-[#047857]' : 'text-[#171a20]'
            )}
          >
            {label}
          </span>
          {description && (
            <span className='mt-1 block text-xs font-normal leading-4 text-[#3d434e]'>
              {description}
            </span>
          )}
        </span>
      </span>
    </button>
  )
}

function QuoteOptionCard({
  tone,
  label,
  description,
  active,
  onClick,
}: {
  tone: 'settled' | 'quote'
  label: string
  description: string
  active: boolean
  onClick: () => void
}) {
  const quoteTone = tone === 'quote'
  const activeClass = quoteTone
    ? 'border-[#ff5a1f] bg-[#fff1ea]'
    : 'border-[#10b981] bg-[#ecfdf5]'
  const activeIconClass = quoteTone
    ? 'bg-[#ff5a1f] text-white'
    : 'bg-[#10b981] text-white'
  const activeLabelClass = quoteTone ? 'text-[#ff5a1f]' : 'text-[#047857]'

  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex min-h-[98px] rounded-xl border p-4 text-left shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors',
        active ? activeClass : 'border-[#e7e9ef] bg-white hover:bg-white'
      )}
    >
      <span className='flex gap-3'>
        <span
          className={cn(
            'flex size-[30px] shrink-0 items-center justify-center rounded-lg',
            active ? activeIconClass : 'bg-[#f5f6fa] text-[#1a1b1e]'
          )}
        >
          {quoteTone ? (
            <FileText className='size-4' strokeWidth={1.6} />
          ) : (
            <Check className='size-4' strokeWidth={1.6} />
          )}
        </span>
        <span className='min-w-0'>
          <span
            className={cn(
              'block text-sm font-medium leading-[1.4]',
              active ? activeLabelClass : 'text-[#171a20]'
            )}
          >
            {label}
          </span>
          <span className='mt-1 block text-xs font-normal leading-4 text-[#3d434e]'>
            {description}
          </span>
        </span>
      </span>
    </button>
  )
}

function QuantityRow({
  name,
  quantityLabel,
  quantity,
  onQuantityChange,
}: {
  name: string
  quantityLabel: string
  quantity: string
  onQuantityChange: (value: string) => void
}) {
  return (
    <div className='flex flex-col gap-3 rounded-2xl bg-[#f5f6fa] px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
      <span className='min-w-0 truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
        {name}
      </span>
      <div className='flex shrink-0 items-center justify-between gap-4 sm:justify-end'>
        <span className='text-sm font-medium leading-[1.4] text-[#6c7285]'>
          {quantityLabel}:
        </span>
        <Input
          value={quantity}
          onChange={e => onQuantityChange(e.target.value)}
          className={cn(
            FORM_FIELD_CLASS,
            'w-[120px] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_1px_6px_rgba(38,45,66,0.08)]'
          )}
        />
      </div>
    </div>
  )
}

// Free-hand signature canvas. Reports the drawn image back as a PNG File plus a
// data URL for preview; the parent uploads it lazily at save time.
function SignaturePad({
  onChange,
  onClear,
  t,
}: {
  onChange: (file: File, dataUrl: string) => void
  onClear: () => void
  t: (key: string) => string
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const last = useRef({ x: 0, y: 0 })
  const [hasInk, setHasInk] = useState(false)

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!
    const rect = c.getBoundingClientRect()
    return {
      x: ((e.clientX - rect.left) * c.width) / rect.width,
      y: ((e.clientY - rect.top) * c.height) / rect.height,
    }
  }

  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = true
    last.current = point(e)
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const ctx = canvasRef.current!.getContext('2d')!
    const p = point(e)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#1E1E1E'
    ctx.beginPath()
    ctx.moveTo(last.current.x, last.current.y)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    last.current = p
    setHasInk(true)
  }
  const up = () => {
    if (!drawing.current) return
    drawing.current = false
    const c = canvasRef.current!
    c.toBlob(blob => {
      if (!blob) return
      onChange(
        new File([blob], 'signature.png', { type: 'image/png' }),
        c.toDataURL()
      )
    })
  }
  const clear = () => {
    const c = canvasRef.current!
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height)
    setHasInk(false)
    onClear()
  }

  return (
    <div className='relative min-h-40 rounded-2xl border border-dashed border-[#cfd5df] bg-[#f5f6fa]'>
      <canvas
        ref={canvasRef}
        width={600}
        height={160}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        className='h-40 w-full touch-none rounded-2xl'
      />
      {!hasInk && (
        <span className='pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-normal leading-[1.4] text-[#6c7285]'>
          {t('report.signatureDrawHere')}
        </span>
      )}
      {hasInk && (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={clear}
          className='absolute right-2 top-2 text-[#6c7285] hover:bg-white'
        >
          {t('report.signatureClear')}
        </Button>
      )}
    </div>
  )
}

// 2.3.9 Create / edit job report — 3-step wizard with exact AC validations.
export default function JobReportWizardPage({
  mode,
  jobId,
  presentation = 'page',
  onClose,
}: {
  mode: 'create' | 'edit'
  jobId?: string
  presentation?: 'page' | 'drawer'
  onClose?: () => void
}) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('jobs')
  const { t: tShared } = useTranslation('shared')
  const { id: routeId = '' } = useParams<{ id: string }>()
  const id = jobId ?? routeId
  const isDrawer = presentation === 'drawer'
  const { data: jobData, isLoading: jobLoading } = useJob(id)
  const { data: reportData, isLoading: reportLoading } = useJobReport(id, true)
  const { data: catalogData } = useReportCatalog(jobData?.data?.service_type)
  const hasReport = !!reportData?.data
  const save = useSaveJobReport(
    id,
    mode === 'edit' || hasReport ? 'edit' : 'create'
  )

  const [step, setStep] = useState(1)
  const [infestation, setInfestation] = useState('')
  const [urgency, setUrgency] = useState('')
  const [affectedAreas, setAffectedAreas] = useState<string[]>([])
  const [signsOfPresence, setSignsOfPresence] = useState<string[]>([])
  const [observation, setObservation] = useState('')
  const [photos, setPhotos] = useState<ReportPhoto[]>([])
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [method, setMethod] = useState('')
  const [products, setProducts] = useState<Product[]>([])
  const [materials, setMaterials] = useState<Material[]>([])
  const [treatedAreas, setTreatedAreas] = useState<string[]>([])
  const [actionsPerformed, setActionsPerformed] = useState('')
  const [timeSpent, setTimeSpent] = useState('')
  const [recommendations, setRecommendations] = useState<string[]>([])
  const [closure, setClosure] = useState('')
  const [needsQuote, setNeedsQuote] = useState(false)
  const [quoteSubject, setQuoteSubject] = useState('')
  const [quoteDescription, setQuoteDescription] = useState('')
  const [nextVisitDate, setNextVisitDate] = useState('')
  const [nextVisitPresetDays, setNextVisitPresetDays] = useState<number | null>(
    null
  )
  const [followUpSlot, setFollowUpSlot] = useState<FollowUpSlot | ''>('')
  const [followUpNotes, setFollowUpNotes] = useState('')
  // Signature: a pending File to upload lazily, a preview URL, and the resolved
  // (or hydrated) file id.
  const [sigMode, setSigMode] = useState<'draw' | 'upload'>('draw')
  const [signatureFile, setSignatureFile] = useState<File | null>(null)
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null)
  const [signatureFileId, setSignatureFileId] = useState<string | null>(null)
  const [signatureRemoved, setSignatureRemoved] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const photoInputRef = useRef<HTMLInputElement>(null)
  const sigInputRef = useRef<HTMLInputElement>(null)
  const job = jobData?.data

  const hydratedRef = useRef(false)
  useEffect(() => {
    if (hydratedRef.current || !reportData?.data) return
    const r = reportData.data
    hydratedRef.current = true
    setInfestation(r.infestation_level ?? '')
    setUrgency(r.urgency_level ?? '')
    setAffectedAreas(r.affected_areas ?? [])
    setSignsOfPresence(r.signs_of_presence ?? [])
    setObservation(r.observation ?? '')
    setMethod(r.treatment ?? '')
    setActionsPerformed(r.actions_performed ?? '')
    setTimeSpent(r.time_spent ?? '')
    setTreatedAreas(r.treated_areas ?? [])
    setRecommendations(r.recommendations ?? [])
    setClosure(r.closure_notes ?? '')
    setNeedsQuote(!!r.needs_quote)
    setQuoteSubject(r.quote_subject ?? '')
    setQuoteDescription(r.quote_description ?? '')
    setNextVisitDate(normalizeToIsoDate(r.next_visit_date))
    setFollowUpNotes(r.next_visit_notes ?? '')
    setSignatureFileId(r.client_signature_file_id ?? null)
    setSignatureRemoved(false)
    // Re-display a previously-saved signature. The preview only renders in
    // 'upload' mode, so switch to it when an existing signature is present.
    setSignaturePreview(r.client_signature_url ?? null)
    setSigMode(r.client_signature_url ? 'upload' : 'draw')
    setProducts(
      (r.products_used ?? []).map(p => ({
        name: String(p.name ?? ''),
        concentration: String(p.concentration ?? ''),
        quantity: String(p.quantity ?? ''),
      }))
    )
    setMaterials(
      (r.materials_used ?? []).map(m => ({
        name: String(m.name ?? ''),
        quantity: String(m.quantity ?? ''),
      }))
    )
    // Prefer the resolved photos (id + signed url + name/mime) so previously
    // uploaded evidence shows as a thumbnail on edit. Fall back to bare ids
    // (no preview) only when the server didn't resolve them.
    setPhotos(
      r.report_photos?.length
        ? r.report_photos.map(p => ({
            fileId: p.id,
            documentId: p.document_id ?? undefined,
            name: p.file_name ?? p.id,
            previewUrl: p.url ?? undefined,
            mime_type: p.mime_type ?? null,
            size: p.size ?? null,
            created_at: null,
          }))
        : (r.report_photo_ids ?? []).map(fileId => ({
            fileId,
            name: fileId,
          }))
    )
  }, [reportData])

  useEffect(() => {
    if (
      job &&
      (job.status === JOB_STATUS.to_plan || job.status === JOB_STATUS.cancelled)
    ) {
      toast({ variant: 'destructive', title: t('report.onlyCompleted') })
      if (onClose) onClose()
      else navigate(`/calendar?job=${id}`)
    }
  }, [job, id, navigate, onClose, t])

  const toggle = (list: string[], value: string) =>
    list.includes(value) ? list.filter(v => v !== value) : [...list, value]

  const updateProduct = (i: number, patch: Partial<Product>) =>
    setProducts(products.map((p, idx) => (idx === i ? { ...p, ...patch } : p)))

  const updateMaterial = (i: number, patch: Partial<Material>) =>
    setMaterials(
      materials.map((m, idx) => (idx === i ? { ...m, ...patch } : m))
    )

  const toggleProductName = (name: string) => {
    setProducts(prev => {
      if (prev.some(product => product.name === name)) {
        return prev.filter(product => product.name !== name)
      }
      if (prev.length >= 10) {
        toast({ variant: 'destructive', title: t('report.errors.maxProducts') })
        return prev
      }
      return [...prev, { name, concentration: '', quantity: '1' }]
    })
  }

  const toggleMaterialName = (name: string) => {
    setMaterials(prev => {
      if (prev.some(material => material.name === name)) {
        return prev.filter(material => material.name !== name)
      }
      if (prev.length >= 10) return prev
      return [...prev, { name, quantity: '1' }]
    })
  }

  const setNextVisitOffset = (days: number) => {
    const next = new Date()
    next.setDate(next.getDate() + days)
    setNextVisitDate(localDateToIsoDate(next))
    setNextVisitPresetDays(days)
  }

  const uploadPhotos = async (files: FileList | File[]) => {
    if (files.length === 0 || uploadingPhoto) return

    const availableSlots = MAX_REPORT_PHOTOS - photos.length
    if (availableSlots <= 0) {
      toast({ variant: 'destructive', title: t('report.errors.maxPhotos') })
      return
    }

    const validFiles: File[] = []
    for (const file of Array.from(files)) {
      if (isFileTooLarge(file)) {
        toast({
          variant: 'destructive',
          title: tShared('attachFiles.tooLarge', {
            max: getFileSizeLimitMb(),
          }),
        })
        continue
      }
      if (!isReportPhotoFile(file)) {
        toast({
          variant: 'destructive',
          title: t('report.errors.photoUpload'),
        })
        continue
      }
      validFiles.push(file)
    }

    if (validFiles.length > availableSlots) {
      toast({ variant: 'destructive', title: t('report.errors.maxPhotos') })
    }
    const filesToUpload = validFiles.slice(0, availableSlots)
    if (filesToUpload.length === 0) return

    const uploadedPhotos: ReportPhoto[] = []
    setUploadingPhoto(true)
    try {
      for (const file of filesToUpload) {
        try {
          const res = await jobsApi.uploadDocument(id, file, 'report_photo')
          const fileId = res.data?.file_id
          if (!fileId) throw new Error('missing file id')
          uploadedPhotos.push({
            fileId,
            documentId: res.data?.id,
            name: file.name,
            previewUrl: res.data?.url ?? undefined,
            mime_type: file.type || null,
            size: file.size,
            created_at: new Date(file.lastModified || Date.now()).toISOString(),
          })
        } catch (err) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(err, t('report.errors.photoUpload')),
          })
        }
      }

      if (uploadedPhotos.length > 0) {
        setPhotos(prev => [...prev, ...uploadedPhotos])
      }
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length > 0) void uploadPhotos(files)
  }
  const handleDrop = async (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDragActive(false)
    if (e.dataTransfer.files.length > 0) {
      await uploadPhotos(e.dataTransfer.files)
    }
  }

  const setDrawnSignature = (file: File, dataUrl: string) => {
    setSignatureFile(file)
    setSignaturePreview(dataUrl)
    setSignatureFileId(null)
    setSignatureRemoved(false)
  }
  const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (isFileTooLarge(file)) {
      toast({
        variant: 'destructive',
        title: tShared('attachFiles.tooLarge', { max: getFileSizeLimitMb() }),
      })
      return
    }
    setSignatureFile(file)
    setSignaturePreview(URL.createObjectURL(file))
    setSignatureFileId(null)
    setSignatureRemoved(false)
  }
  const clearSignature = () => {
    setSignatureRemoved(!!signatureFileId || !!signaturePreview)
    setSignatureFile(null)
    setSignaturePreview(null)
    setSignatureFileId(null)
  }

  // Upload a pending signature file (if any) and return the resolved file id.
  const resolveSignatureId = async (): Promise<string | undefined> => {
    if (signatureFile) {
      const res = await jobsApi.uploadDocument(id, signatureFile, 'signature')
      const fileId = res.data?.file_id
      if (fileId) {
        setSignatureFile(null)
        setSignatureFileId(fileId)
        return fileId
      }
    }
    return signatureFileId ?? undefined
  }

  const nextFromStep1 = () => {
    if (!infestation || !urgency) {
      setErrors({ step1: t('report.errors.completeRequired') })
      return
    }
    setErrors({})
    setStep(2)
  }
  const nextFromStep2 = () => {
    if (!method) {
      setErrors({ step2: t('report.errors.completeRequired') })
      return
    }
    const incomplete = products.some(p => !p.name || !p.quantity)
    if (incomplete) {
      setErrors({ products: t('report.errors.completeProducts') })
      return
    }
    // A selected material must carry a quantity (2.3.9): block name-without-qty.
    const materialsIncomplete = materials.some(m => m.name && !m.quantity)
    if (materialsIncomplete) {
      setErrors({ materials: t('report.errors.completeMaterials') })
      return
    }
    setErrors({})
    setStep(3)
  }

  const buildReportPayload = (
    signatureId?: string,
    status: 'draft' | 'completed' = 'completed'
  ) => ({
    service_type: job?.service_type ?? undefined,
    infestation_level: infestation || undefined,
    urgency_level: urgency || undefined,
    affected_areas: affectedAreas.length ? affectedAreas : undefined,
    signs_of_presence: signsOfPresence.length ? signsOfPresence : undefined,
    observation,
    treatment: method,
    actions_performed: actionsPerformed || undefined,
    time_spent: timeSpent || undefined,
    products_used: products.map(p => ({ ...p })),
    materials_used: materials.filter(m => m.name).map(m => ({ ...m })),
    treated_areas: treatedAreas.length ? treatedAreas : undefined,
    recommendations: recommendations.length ? recommendations : undefined,
    closure_notes: closure,
    needs_quote: needsQuote,
    quote_subject: needsQuote ? quoteSubject || undefined : undefined,
    quote_description:
      needsQuote && quoteDescription ? quoteDescription : undefined,
    next_visit_date: nextVisitDate || undefined,
    next_visit_notes: followUpNotes || undefined,
    report_photo_ids: photos.map(p => p.fileId),
    client_signature_file_id: signatureRemoved ? null : signatureId,
    status,
  })

  const submit = async () => {
    setErrors({})
    try {
      const sigId = await resolveSignatureId()
      await save.mutateAsync(buildReportPayload(sigId, 'completed'))
      toast({ variant: 'success', title: t('report.submitSuccess') })
      if (onClose) onClose()
      else navigate(`/calendar?job=${id}&tab=reports`)
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('report.submitError')),
      })
    }
  }

  const closeWizard = () => {
    if (onClose) onClose()
    else navigate(`/calendar?job=${id}`)
  }
  const goBack = () => (step === 1 ? closeWizard() : setStep(step - 1))
  const goNext = () => {
    if (step === 1) nextFromStep1()
    else if (step === 2) nextFromStep2()
    else submit()
  }

  if (jobLoading || (mode === 'edit' && reportLoading))
    return <Skeleton className='h-96 w-full' />

  const steps = [
    t('report.stepObservation'),
    t('report.stepTreatment'),
    t('report.stepClosure'),
  ]

  // 2.3.7 / 2.3.9 — per-service-category option lists. When the job's service
  // maps to a catalogue category the backend returns option groups already
  // localized to the request language; otherwise (unrecognised service) we fall
  // back to the generic constants rendered through i18n keys.
  const catalogEntry = catalogData?.data?.categories?.[0]
  const usingCatalog = !!catalogEntry
  const areaOptions = catalogEntry
    ? catalogEntry.affected_areas
    : [...REPORT_AFFECTED_AREAS]
  const signOptions = catalogEntry
    ? catalogEntry.signs_of_presence
    : [...REPORT_SIGNS]
  const methodOptions = catalogEntry
    ? catalogEntry.treatment_methods
    : [...REPORT_METHODS]
  // Product "Name" (catalogue-driven, per WBS 2.3.9) and "Material name"
  // dropdown options. Type stays the generic product-type list below.
  const productNameOptions = catalogEntry?.products_used ?? []
  const equipmentOptions = catalogEntry?.equipment_used ?? []
  const recommendationOptions = catalogEntry
    ? catalogEntry.recommendations
    : [...REPORT_RECOMMENDATIONS]
  // Catalogue labels come pre-localized; generic constants need translating.
  const optLabel = (option: string, keyPrefix: string) =>
    usingCatalog ? option : t(`${keyPrefix}.${option}`)
  const allRecommendations =
    recommendationOptions.length > 0 &&
    recommendations.length === recommendationOptions.length
  const productOptions = productNameOptions.length
    ? productNameOptions
    : products.map(product => product.name).filter(Boolean)
  const materialOptions = equipmentOptions.length
    ? equipmentOptions
    : materials.map(material => material.name).filter(Boolean)
  const serviceTime =
    job?.start_time && job?.end_time
      ? `${formatReportClockTime(job.start_time)} - ${formatReportClockTime(job.end_time)}`
      : null
  const clientTypeValue = job?.client?.type
  const clientType = clientTypeValue
    ? t(`report.clientTypes.${clientTypeValue}`, {
        defaultValue: formatClientType(clientTypeValue) ?? clientTypeValue,
      })
    : null
  const metaItems = [job?.reference ?? id, clientType, serviceTime].filter(
    Boolean
  )
  const photoItems: AttachmentRef[] = photos.map(photo => ({
    id: photo.documentId ?? photo.fileId,
    file_id: photo.fileId,
    name: photo.name,
    url: photo.previewUrl ?? null,
    mime_type: photo.mime_type ?? null,
    size: photo.size ?? null,
    created_at: photo.created_at ?? null,
    source: 'upload',
  }))
  const followUpSlotOptions: Array<{
    value: FollowUpSlot
    label: string
  }> = [
    { value: 'morning', label: t('report.morningSlot') },
    { value: 'afternoon', label: t('report.afternoonSlot') },
    { value: 'indifferent', label: t('report.indifferent') },
  ]
  const selectedFollowUpSlotLabel =
    followUpSlotOptions.find(option => option.value === followUpSlot)?.label ??
    ''

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <div className='space-y-4'>
          <SectionCard title={t('report.infestationLevel')} required>
            <RadioCardSelect
              options={REPORT_INFESTATION}
              value={infestation}
              onChange={setInfestation}
              cols='grid-cols-2 sm:grid-cols-4'
              label={o => t(`report.infestation.${o}`)}
              renderIcon={o => <RadioDot option={o} />}
            />
          </SectionCard>

          <SectionCard
            title={t('report.affectedAreas')}
            count={affectedAreas.length}
          >
            <div className='flex flex-wrap gap-2'>
              {areaOptions.map(o => (
                <CheckPill
                  key={o}
                  label={optLabel(o, 'report.areas')}
                  active={affectedAreas.includes(o)}
                  onClick={() => setAffectedAreas(toggle(affectedAreas, o))}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={t('report.signsOfPresence')}
            count={signsOfPresence.length}
          >
            <div className='flex flex-wrap gap-2'>
              {signOptions.map(o => (
                <CheckPill
                  key={o}
                  label={optLabel(o, 'report.signs')}
                  active={signsOfPresence.includes(o)}
                  onClick={() => setSignsOfPresence(toggle(signsOfPresence, o))}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={t('report.observations')}
            bodyClassName='rounded-none bg-transparent p-0'
          >
            <div className='pt-2'>
              <Textarea
                rows={3}
                maxLength={2000}
                placeholder={t('report.observationsPlaceholder')}
                value={observation}
                onChange={e => setObservation(e.target.value)}
                className={cn('resize-none', FORM_TEXTAREA_CLASS)}
              />
            </div>
          </SectionCard>

          <SectionCard title={t('report.customerUrgencyLevel')} required>
            <RadioCardSelect
              options={REPORT_URGENCY}
              value={urgency}
              onChange={setUrgency}
              cols='grid-cols-2 sm:grid-cols-4'
              label={o => t(`report.urgency.${o}`)}
              renderIcon={o => <RadioDot option={o} />}
            />
          </SectionCard>

          <SectionCard
            title={t('report.photosEvidence')}
            bodyClassName='rounded-none bg-transparent p-0'
          >
            <div className='space-y-4 pt-2'>
              <FileUploadDropzone
                className='min-h-[86px] rounded-2xl px-5 py-4'
                onClick={() => photoInputRef.current?.click()}
                onDragOver={e => {
                  e.preventDefault()
                  setDragActive(true)
                }}
                onDragLeave={e => {
                  e.preventDefault()
                  setDragActive(false)
                }}
                onDrop={handleDrop}
                disabled={uploadingPhoto || photos.length >= MAX_REPORT_PHOTOS}
                loading={uploadingPhoto}
                loadingText={tShared('attachFiles.uploading')}
                dragActive={dragActive}
                primaryText={t('report.photosEvidenceDropHint')}
                secondaryText={t('report.photosEvidenceFormatHint')}
              />
              <input
                ref={photoInputRef}
                type='file'
                multiple
                accept={REPORT_PHOTO_ACCEPT}
                className='hidden'
                disabled={uploadingPhoto || photos.length >= MAX_REPORT_PHOTOS}
                onChange={handlePhotoSelect}
              />
              <AttachmentPreviewGrid
                items={photoItems}
                removeLabel={t('report.removePhoto')}
                disabled={uploadingPhoto}
                onRename={async (item, name) => {
                  const fileId = item.file_id ?? item.id
                  const photo = photos.find(
                    current => current.fileId === fileId
                  )
                  if (!photo?.documentId) {
                    throw new Error('Missing job document id for report photo')
                  }
                  const response = await jobsApi.renameDocument(
                    id,
                    photo.documentId,
                    name
                  )
                  const renamedName = response.data?.file_name ?? photo.name
                  const renamedUrl = response.data?.url ?? photo.previewUrl
                  setPhotos(current =>
                    current.map(candidate =>
                      candidate.fileId === photo.fileId
                        ? {
                            ...candidate,
                            name: renamedName,
                            previewUrl: renamedUrl ?? undefined,
                          }
                        : candidate
                    )
                  )
                  toast({
                    variant: 'success',
                    title: t('attachments.renameSuccess'),
                  })
                }}
                onRemove={item =>
                  setPhotos(prev =>
                    prev.filter(
                      photo => photo.fileId !== (item.file_id ?? item.id)
                    )
                  )
                }
                onItemClick={item => {
                  if (item.url) window.open(item.url, '_blank', 'noopener')
                }}
              />
            </div>
          </SectionCard>

          {errors.step1 && (
            <p className='text-xs text-destructive'>{errors.step1}</p>
          )}
        </div>
      )
    }

    if (step === 2) {
      return (
        <div className='space-y-4'>
          <SectionCard title={t('report.treatmentMethod')} required>
            <RadioCardSelect
              options={methodOptions}
              value={method}
              onChange={setMethod}
              cols='grid-cols-2 sm:grid-cols-5'
              label={o => optLabel(o, 'report.methods')}
              activeClassName='border-[#10b981] bg-[#ecfdf5] text-[#171a20]'
              renderIcon={(_option, active) =>
                active ? (
                  <Check className='h-4 w-4 shrink-0 text-[#10b981]' />
                ) : null
              }
            />
          </SectionCard>

          <SectionCard
            title={t('report.productsUsed')}
            count={products.filter(product => product.name).length}
            bodyClassName='rounded-none bg-transparent p-0'
          >
            <div className='space-y-4'>
              <div className='flex flex-wrap gap-2 rounded-2xl bg-[#f5f6fa] p-4'>
                {productOptions.map(option => (
                  <CheckPill
                    key={option}
                    label={option}
                    active={products.some(product => product.name === option)}
                    onClick={() => toggleProductName(option)}
                    icon={<OptionAssetIcon src={productUsedIcon} />}
                    showCheck={false}
                  />
                ))}
              </div>
              {products
                .filter(product => product.name)
                .map(product => (
                  <QuantityRow
                    key={product.name}
                    name={product.name}
                    quantityLabel={t('report.product.quantity')}
                    quantity={product.quantity}
                    onQuantityChange={quantity =>
                      updateProduct(
                        products.findIndex(item => item.name === product.name),
                        { quantity }
                      )
                    }
                  />
                ))}
              {errors.products && (
                <p className='text-xs text-destructive'>{errors.products}</p>
              )}
            </div>
          </SectionCard>

          <SectionCard
            title={t('report.materialUsed')}
            count={materials.filter(material => material.name).length}
            bodyClassName='rounded-none bg-transparent p-0'
          >
            <div className='space-y-4'>
              <div className='flex flex-wrap gap-2 rounded-2xl bg-[#f5f6fa] p-4'>
                {materialOptions.map(option => (
                  <CheckPill
                    key={option}
                    label={option}
                    active={materials.some(
                      material => material.name === option
                    )}
                    onClick={() => toggleMaterialName(option)}
                    icon={<OptionAssetIcon src={materialUsedIcon} />}
                    showCheck={false}
                  />
                ))}
              </div>
              {materials
                .filter(material => material.name)
                .map(material => (
                  <QuantityRow
                    key={material.name}
                    name={material.name}
                    quantityLabel={t('report.material.quantity')}
                    quantity={material.quantity}
                    onQuantityChange={quantity =>
                      updateMaterial(
                        materials.findIndex(
                          item => item.name === material.name
                        ),
                        { quantity }
                      )
                    }
                  />
                ))}
              {errors.materials && (
                <p className='text-xs text-destructive'>{errors.materials}</p>
              )}
            </div>
          </SectionCard>

          <SectionCard title={t('report.treatedArea')}>
            <div className='flex flex-wrap gap-2'>
              {REPORT_TREATED_AREAS.map(o => (
                <CheckPill
                  key={o}
                  label={t(`report.treatedAreasOpts.${o}`)}
                  active={treatedAreas.includes(o)}
                  onClick={() => setTreatedAreas(toggle(treatedAreas, o))}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={t('report.actionPerformed')}
            bodyClassName='rounded-none bg-transparent p-0'
          >
            <div className='pt-2'>
              <Textarea
                rows={3}
                maxLength={2000}
                placeholder={t('report.observationsPlaceholder')}
                value={actionsPerformed}
                onChange={e => setActionsPerformed(e.target.value)}
                className={cn('resize-none', FORM_TEXTAREA_CLASS)}
              />
            </div>
          </SectionCard>

          {errors.step2 && (
            <p className='text-xs text-destructive'>{errors.step2}</p>
          )}
        </div>
      )
    }

    return (
      <div className='space-y-4'>
        <SectionCard
          title={t('report.clientRecommendations')}
          count={recommendations.length}
          bodyClassName='rounded-none bg-transparent p-0'
          action={
            <button
              type='button'
              onClick={() =>
                setRecommendations(
                  allRecommendations ? [] : [...recommendationOptions]
                )
              }
              className='text-sm font-medium leading-[1.4] text-[#ff5a1f] hover:underline'
            >
              {allRecommendations
                ? t('report.deselectAll')
                : t('report.selectAll')}
            </button>
          }
        >
          <div className='grid grid-cols-1 gap-2 pt-2 sm:grid-cols-2'>
            {recommendationOptions.map(o => (
              <SelectableCard
                key={o}
                compact
                label={optLabel(o, 'report.recommendations')}
                active={recommendations.includes(o)}
                onClick={() => setRecommendations(toggle(recommendations, o))}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard
          title={t('report.quoteRequiredShort')}
          bodyClassName='rounded-none bg-transparent p-0'
        >
          <div className='grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2'>
            <QuoteOptionCard
              tone='settled'
              label={t('report.noQuoteTitle')}
              description={t('report.noQuoteDescription')}
              active={!needsQuote}
              onClick={() => setNeedsQuote(false)}
            />
            <QuoteOptionCard
              tone='quote'
              label={t('report.yesQuoteTitle')}
              description={t('report.yesQuoteDescription')}
              active={needsQuote}
              onClick={() => setNeedsQuote(true)}
            />
          </div>
          {needsQuote && (
            <div className='mt-3 space-y-3'>
              <Textarea
                rows={3}
                maxLength={2000}
                placeholder={t('report.quoteDescriptionPlaceholder')}
                value={quoteDescription}
                onChange={e => setQuoteDescription(e.target.value)}
                className={cn('resize-none', FORM_TEXTAREA_CLASS)}
              />
              <div className='flex min-h-11 items-center gap-2 rounded-xl border border-[#2e5ce6] bg-[#ebf1fe] px-3 py-3 text-[#2550cc]'>
                <Info className='size-5 shrink-0' strokeWidth={2} />
                <p className='text-sm font-normal leading-[1.4]'>
                  {t('report.quoteDraftNote')}
                </p>
              </div>
            </div>
          )}
        </SectionCard>

        <SectionCard
          title={t('report.nextVisitTitle')}
          bodyClassName='rounded-none bg-transparent p-0'
        >
          <div className='space-y-4 pt-2'>
            <div className='flex flex-wrap gap-3 rounded-2xl bg-[#f5f6fa] p-4'>
              {[7, 10, 14].map(days => (
                <button
                  key={days}
                  type='button'
                  onClick={() => setNextVisitOffset(days)}
                  className={cn(
                    'inline-flex h-10 items-center gap-2 rounded-xl border bg-white px-4 text-xs font-normal leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05),0_1px_6px_rgba(38,45,66,0.08)]',
                    nextVisitPresetDays === days &&
                      'border-[#10b981] bg-[#ecfdf5] text-[#171a20]'
                  )}
                >
                  {nextVisitPresetDays === days && (
                    <Check className='size-4 text-[#10b981]' />
                  )}
                  {days === 7
                    ? t('report.nextVisitInWeek')
                    : t('report.nextVisitInDays', { count: days })}
                </button>
              ))}
            </div>

            <div className='space-y-2'>
              <Label
                htmlFor='nextDate'
                className='text-sm font-normal leading-[1.4] text-[#171a20]'
              >
                {t('report.scheduleDate')}
              </Label>
              <DatePicker
                id='nextDate'
                value={nextVisitDate}
                onChange={value => {
                  setNextVisitDate(value)
                  setNextVisitPresetDays(null)
                }}
                variant='field'
                className={FORM_DATE_FIELD_CLASS}
              />
            </div>

            <div className='space-y-3 rounded-2xl bg-[#f5f6fa] p-4'>
              <p className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {t('report.recommendedTimeSlot')}
              </p>
              <div className='flex flex-wrap gap-3'>
                {followUpSlotOptions.map(option => (
                  <button
                    key={option.value}
                    type='button'
                    onClick={() => setFollowUpSlot(option.value)}
                    className={cn(
                      'inline-flex h-10 items-center gap-2 rounded-xl border bg-white px-4 text-xs font-normal leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05),0_1px_6px_rgba(38,45,66,0.08)]',
                      followUpSlot === option.value &&
                        'border-[#10b981] bg-[#ecfdf5]'
                    )}
                  >
                    {followUpSlot === option.value && (
                      <Check className='size-4 text-[#10b981]' />
                    )}
                    {option.label}
                  </button>
                ))}
              </div>

              {(nextVisitDate || followUpSlot) && (
                <div className='flex gap-3 rounded-xl bg-[#ecfdf5] p-4 text-[#047857]'>
                  <CalendarDays className='mt-0.5 size-5 shrink-0' />
                  <div className='text-sm font-normal leading-[1.4]'>
                    <p className='font-medium'>{t('report.trackingSheet')}</p>
                    <p>
                      {formatTrackingDate(
                        nextVisitDate,
                        selectedFollowUpSlotLabel,
                        tShared('datePicker.placeholder'),
                        i18n.resolvedLanguage ?? i18n.language
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title={t('report.followUpNotes')}
          bodyClassName='rounded-none bg-transparent p-0'
        >
          <div className='pt-2'>
            <Textarea
              rows={3}
              placeholder={t('report.followUpNotesPlaceholder')}
              value={followUpNotes}
              onChange={e => setFollowUpNotes(e.target.value)}
              className={cn('resize-none', FORM_TEXTAREA_CLASS)}
            />
          </div>
        </SectionCard>

        <SectionCard
          title={t('report.customerSignature')}
          bodyClassName='rounded-none bg-transparent p-0'
        >
          <div className='mb-3 flex gap-2 pt-2'>
            {(['upload', 'draw'] as const).map(m => (
              <button
                key={m}
                type='button'
                onClick={() => setSigMode(m)}
                className={cn(
                  'h-9 rounded-xl border px-4 text-xs font-normal leading-4 transition-colors',
                  sigMode === m
                    ? 'border-[#ff5a1f] bg-[#ff5a1f] text-white'
                    : 'border-[#e7e9ef] bg-white text-[#171a20] hover:bg-[#f5f6fa]'
                )}
              >
                {m === 'upload'
                  ? t('report.signatureUpload')
                  : t('report.signatureDraw')}
              </button>
            ))}
          </div>

          {sigMode === 'draw' ? (
            <SignaturePad
              onChange={setDrawnSignature}
              onClear={clearSignature}
              t={t}
            />
          ) : (
            <div className='space-y-3'>
              <div
                role='button'
                tabIndex={0}
                onClick={() => sigInputRef.current?.click()}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ')
                    sigInputRef.current?.click()
                }}
                className='flex min-h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-[#cfd5df] bg-[#f5f6fa] px-4 py-8 text-center transition-colors hover:bg-[#eceef2]'
              >
                <UploadCloud className='h-6 w-6 text-[#6c7285]' />
                <p className='text-sm font-normal leading-[1.4] text-[#6c7285]'>
                  {t('report.dropFilesHint')}
                </p>
              </div>
              <input
                ref={sigInputRef}
                type='file'
                accept='image/png,image/jpeg,image/jpg'
                className='hidden'
                onChange={handleSignatureUpload}
              />
              {signaturePreview && (
                <div className='relative inline-block max-w-full'>
                  <img
                    src={signaturePreview}
                    alt='signature'
                    className='max-h-28 max-w-full object-contain'
                  />
                  <button
                    type='button'
                    aria-label={t('report.signatureClear')}
                    onClick={clearSignature}
                    className='absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-[#171a20] shadow hover:bg-white'
                  >
                    <X className='h-3.5 w-3.5' />
                  </button>
                </div>
              )}
            </div>
          )}
          <p className='mt-3 text-xs font-normal leading-[1.4] text-[#6c7285]'>
            {t('report.signatureHint')}
          </p>
        </SectionCard>
      </div>
    )
  }

  const surfaceClass = isDrawer ? 'mx-auto w-full' : 'mx-auto max-w-6xl'

  return (
    <div
      className={cn(
        isDrawer
          ? 'h-full min-h-0 overflow-y-auto bg-[#f5f6fa] px-4 py-3'
          : 'space-y-5'
      )}
    >
      <div className={cn(surfaceClass, 'mb-3')}>
        <Button
          type='button'
          variant='outline'
          onClick={closeWizard}
          className='h-10 rounded-xl border-[#dfe3ea] bg-white px-3 text-sm font-medium leading-none text-[#171a20] shadow-none'
        >
          <ArrowLeft className='mr-2 size-4' strokeWidth={1.6} />
          {t('report.backToIntervention')}
        </Button>
      </div>

      <div
        className={cn(
          surfaceClass,
          'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white'
        )}
      >
        <div className='px-5 py-4'>
          <div className='flex items-start justify-between gap-4'>
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <span className='rounded-md bg-[#d1fae5] px-2 py-1 font-sans text-xs font-semibold uppercase leading-none text-[#047857]'>
                  {t('report.headerBadge')}
                </span>
                {job?.service_type && (
                  <span className='inline-flex min-w-0 items-center gap-1.5 rounded-md bg-[#f5f6fa] px-2 py-1 text-xs font-medium leading-none text-[#171a20]'>
                    <ServiceCategoryIcon
                      name={job.service_type}
                      className='size-3.5 shrink-0'
                    />
                    <span className='truncate'>{job.service_type}</span>
                  </span>
                )}
              </div>
              <h1 className='mt-2 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
                {t('report.headerTitle')}
              </h1>
              <p className='mt-1 flex min-w-0 items-center gap-1.5 truncate text-xs font-medium leading-[1.4] text-[#6c7285]'>
                {metaItems.map((item, index) => (
                  <span key={`${item}-${index}`} className='contents'>
                    {index > 0 && (
                      <span className='shrink-0 text-xs font-medium leading-none text-[#6c7285]'>
                        &middot;
                      </span>
                    )}
                    <span className='min-w-0 truncate'>{item}</span>
                  </span>
                ))}
              </p>
            </div>
          </div>
        </div>

        <div className='border-t border-[#e7e9ef] px-8 py-5'>
          <div className='relative'>
            <div className='absolute left-[44px] right-[44px] top-4 grid grid-cols-2'>
              {[0, 1].map(index => (
                <span
                  key={index}
                  className={cn(
                    'h-px',
                    index < step - 1 ? 'bg-[#ff5a1f]' : 'bg-[#e7e9ef]'
                  )}
                />
              ))}
            </div>
            <div className='relative z-10 flex w-full items-start justify-between'>
              {steps.map((label, index) => {
                const number = index + 1
                const active = step === number
                const done = step > number
                return (
                  <div
                    key={label}
                    className='flex w-[88px] flex-col items-center gap-2'
                  >
                    <span
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full border text-sm font-semibold leading-4',
                        done && 'border-[#10b981] bg-[#10b981] text-white',
                        active && 'border-[#ff5a1f] bg-[#ff5a1f] text-white',
                        !active &&
                          !done &&
                          'border-[#cfd5df] bg-white text-[#171a20]'
                      )}
                    >
                      {done ? (
                        <Check className='size-4' strokeWidth={3} />
                      ) : (
                        number
                      )}
                    </span>
                    <span
                      className={cn(
                        'font-sans text-xs font-normal leading-4',
                        done && 'text-[#047857]',
                        active && 'text-[#ff5a1f]',
                        !active && !done && 'text-[#171a20]'
                      )}
                    >
                      {label}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <div className='border-t border-[#e7e9ef] px-5 py-5'>
          {renderStepContent()}
        </div>
      </div>

      <div
        className={cn(
          surfaceClass,
          'mt-4 rounded-2xl border border-[#e7e9ef] bg-white px-4 py-3 shadow-[0_1px_4px_rgba(38,45,66,0.05),0_4px_14px_rgba(38,45,66,0.08)]'
        )}
      >
        <div
          className={cn(
            'flex items-center',
            step === 1 ? 'justify-end' : 'justify-between'
          )}
        >
          {step > 1 && (
            <Button
              type='button'
              variant='outline'
              onClick={goBack}
              disabled={save.isPending}
              className='h-8 rounded-lg border-[#e7e9ef] bg-white px-3 text-sm font-medium leading-none text-[#171a20] shadow-none'
            >
              <ArrowLeft className='mr-2 size-4' strokeWidth={1.6} />
              {t('report.previous')}
            </Button>
          )}
          <Button
            type='button'
            onClick={goNext}
            disabled={save.isPending}
            className='h-10 rounded-xl bg-[#ff5a1f] px-4 text-sm font-medium leading-none text-white hover:bg-[#ef4f16]'
          >
            {step === 3 ? (
              <>
                <Check className='mr-2 size-4' strokeWidth={2.2} />
                {t('report.submitReportCta')}
              </>
            ) : (
              <>
                {t('report.nextStep')}
                <ArrowRight className='ml-2 size-4' strokeWidth={1.6} />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
